import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, IconButton, Drawer, useMediaQuery, useTheme as useMuiTheme } from '@mui/material';
import { Close as CloseIcon, Link as LinkIcon, FavoriteBorder as FavoriteIcon, Share as ShareIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Story } from '../types/story';
import { useTheme } from '../context/ThemeContext';
import SEO from '../components/SEO';
import Header from '../components/Header';
import MartiniBanner from '../components/MartiniBanner';
import { STRIPE_PAYMENT_LINK } from '../config/stripe';
import { getPublishedStories } from '../data';
import { trackStoryView } from '../utils/analytics';

const Home: React.FC = () => {
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const stories = getPublishedStories();
  const [selectedStory, setSelectedStory] = useState<Story | null>(stories[0] || null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const contentScrollRef = useRef<HTMLDivElement>(null);
  const storyListScrollRef = useRef<HTMLDivElement>(null);
  const mainContentRef = useRef<HTMLDivElement>(null);

  // Scroll to top and reset scroll state when story changes
  useEffect(() => {
    setIsScrolled(false);
    if (mainContentRef.current) {
      mainContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    if (contentScrollRef.current) {
      contentScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [selectedStory]);

  // Detect scroll to hide hero
  useEffect(() => {
    const handleScroll = () => {
      if (contentScrollRef.current) {
        const scrollTop = contentScrollRef.current.scrollTop;
        setIsScrolled(scrollTop > 20);
      }
    };

    const scrollContainer = contentScrollRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [selectedStory]);

  const colors = {
    bg: isDark ? '#0f0f0f' : '#f5f5f5',
    cardBg: isDark ? '#1a1a1a' : '#ffffff',
    headerBg: isDark ? '#161616' : '#fafafa',
    text: isDark ? '#e0e0e0' : '#1a1a1a',
    textSecondary: isDark ? '#999' : '#666',
    textMuted: isDark ? '#555' : '#aaa',
    border: isDark ? '#1a1a1a' : '#f0f0f0',
    hover: isDark ? '#1c1c1c' : '#f8f8f8',
    selected: isDark ? '#1c1c1c' : '#fafafa',
    selectedBorder: isDark ? '#252525' : '#ebebeb',
  };

  // Check if a story is today's story
  const isTodaysStory = (story: Story) => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return story.publishDate === today;
  };

  const handleStorySelect = (story: Story) => {
    // Save the current scroll position of the story list
    const currentScrollPosition = storyListScrollRef.current?.scrollTop || 0;
    
    setSelectedStory(story);
    
    // Track story view
    trackStoryView(story.id, story.title, story.city, story.intensity);
    
    // Restore the scroll position after state update
    requestAnimationFrame(() => {
      if (storyListScrollRef.current) {
        storyListScrollRef.current.scrollTop = currentScrollPosition;
      }
    });
    
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const handleCopyPermalink = () => {
    if (selectedStory) {
      const permalink = `/story/${selectedStory.id}`;
      window.open(permalink, '_blank');
    }
  };

  // Generate fake engagement stats based on story ID
  const getEngagementStats = (storyId: string) => {
    const hash = storyId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const likes = 200 + (hash % 3000);
    const shares = 20 + (hash % 800);
    return { likes, shares };
  };

  const StoryList = () => (
    <Box sx={{ 
      width: { xs: '100%', md: '380px' },
      borderRight: `1px solid ${colors.border}`,
      backgroundColor: colors.headerBg,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      height: '100%'
    }}>
      {/* Drawer Header (Mobile Only) */}
      {isMobile && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: `1px solid ${colors.border}`
        }}>
          <Typography variant="h6" sx={{ fontWeight: 500, color: colors.text }}>
            Stories
          </Typography>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: colors.text }}>
            <CloseIcon />
          </IconButton>
        </Box>
      )}
        {/* Story List */}
        <Box 
          ref={storyListScrollRef}
          sx={{ 
            flex: 1,
            overflowY: 'auto',
            pt: 3,
            scrollbarWidth: 'thin',
            scrollbarColor: isDark ? '#333 transparent' : '#d0d0d0 transparent',
            '&::-webkit-scrollbar': {
              width: '3px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: isDark ? '#333' : '#d0d0d0',
              borderRadius: '2px',
              '&:hover': {
                backgroundColor: isDark ? '#444' : '#b0b0b0',
              },
            },
          }}>
          {stories.map((story, index) => {
            const isToday = isTodaysStory(story);
            const isSelected = selectedStory?.id === story.id;
            
            return (
            <Box
              key={story.id}
              onClick={() => handleStorySelect(story)}
              sx={{
                p: 3,
                cursor: 'pointer',
                borderBottom: `1px solid ${colors.border}`,
                backgroundColor: isSelected 
                  ? colors.selected 
                  : isToday 
                    ? (isDark ? 'rgba(192, 132, 252, 0.06)' : 'rgba(147, 51, 234, 0.04)')
                    : 'transparent',
                borderLeft: isSelected 
                  ? `2px solid ${colors.selectedBorder}` 
                  : isToday
                    ? `1px solid ${isDark ? 'rgba(192, 132, 252, 0.4)' : 'rgba(147, 51, 234, 0.3)'}`
                    : '2px solid transparent',
                boxShadow: isToday && !isSelected
                  ? (isDark ? '0 0 20px rgba(192, 132, 252, 0.08)' : '0 0 20px rgba(147, 51, 234, 0.06)')
                  : 'none',
                transition: 'all 0.2s ease',
                opacity: isSelected ? 1 : 0.5,
                '&:hover': {
                  backgroundColor: colors.hover,
                  opacity: 1,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    fontWeight: 400,
                    color: isSelected ? colors.textSecondary : colors.textMuted,
                    fontSize: '0.7rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase'
                  }}
                >
                  {story.city}
                </Typography>
                {isToday && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 0.5,
                    px: 1.25,
                    py: 0.4,
                    background: isDark 
                      ? 'linear-gradient(135deg, rgba(192, 132, 252, 0.15) 0%, rgba(147, 51, 234, 0.1) 100%)'
                      : 'linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(192, 132, 252, 0.08) 100%)',
                    border: `1px solid ${isDark ? 'rgba(192, 132, 252, 0.3)' : 'rgba(147, 51, 234, 0.25)'}`,
                    borderRadius: '12px',
                    backdropFilter: 'blur(8px)'
                  }}>
                    <Typography sx={{ fontSize: '0.6rem', color: isDark ? '#E9D5FF' : '#7C3AED', fontWeight: 500, letterSpacing: '1px' }}>Today</Typography>
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 400,
                    color: isSelected ? colors.text : colors.textMuted,
                    fontSize: '1rem',
                    lineHeight: 1.4,
                    flex: 1
                  }}
                >
                  {story.title}
                </Typography>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    gap: 0.25, 
                    alignItems: 'center',
                    px: 0.75,
                    py: 0.25,
                    backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                    borderRadius: '8px',
                    flexShrink: 0
                  }}
                >
                  {story.intensity === 0 ? (
                    <Typography sx={{ fontSize: '0.6rem', color: colors.textSecondary }}>
                      Non-erotic
                    </Typography>
                  ) : (
                    [1, 2, 3].map((level) => (
                      <Typography
                        key={level}
                        sx={{
                          fontSize: '0.65rem',
                          opacity: level <= story.intensity ? 1 : 0.25,
                          transition: 'all 0.2s ease'
                        }}
                      >
                        🔥
                      </Typography>
                    ))
                  )}
                </Box>
              </Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: isSelected ? colors.textSecondary : colors.textMuted,
                  fontSize: '0.85rem',
                  lineHeight: 1.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  mb: 1
                }}
              >
                {story.excerpt}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: colors.textSecondary,
                  fontSize: '0.7rem'
                }}
              >
                {format(new Date(story.publishDate), 'MMM d, yyyy')} · {story.readTime}
              </Typography>
            </Box>
            );
          })}
        </Box>
    </Box>
  );

  // Generate structured data for SEO
  const websiteStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Daily Stories",
    "description": "Intense erotic stories exploring forbidden desires, fetish fantasies, and taboo encounters. Explicit adult fiction published daily. 18+ only.",
    "url": "https://dailystories.fr",
    "publisher": {
      "@type": "Organization",
      "name": "Daily Stories",
      "url": "https://dailystories.fr",
      "logo": {
        "@type": "ImageObject",
        "url": "https://dailystories.fr/logo512.png"
      }
    },
    "potentialAction": {
      "@type": "ReadAction",
      "target": "https://dailystories.fr"
    },
    "inLanguage": "en"
  };

  const itemListStructuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Latest Erotic Stories",
    "description": "Recent intimate fiction published on Daily Stories",
    "numberOfItems": stories.length,
    "itemListElement": stories.slice(0, 10).map((story, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Article",
        "headline": story.title,
        "description": story.excerpt,
        "url": `https://dailystories.fr/story/${story.id}`,
        "datePublished": story.publishDate,
        "author": {
          "@type": "Person",
          "name": "Marin"
        }
      }
    }))
  };

  return (
    <>
      <SEO 
        title="Daily Stories – Intense Erotic Fiction | Explicit Adult Stories & Fetish Tales"
        description="Intense erotic stories published daily. Explicit adult fiction exploring forbidden desires, fetish fantasies, BDSM, power dynamics & taboo encounters. Literary erotica for adventurous readers. 18+ only."
        keywords="intense erotic stories, explicit adult fiction, fetish stories, bdsm erotica, taboo erotica, forbidden stories, power exchange fiction, kinky stories, adult fetish tales, explicit erotica, hardcore adult fiction, erotic literature, daily adult stories, intense sexual fiction"
        url="https://dailystories.fr"
      />
      
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteStructuredData) }}
      />
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListStructuredData) }}
      />
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        backgroundColor: colors.bg,
        overflow: 'hidden'
      }}>
        {/* Header */}
        <Header isMobile={isMobile} onMenuClick={() => setDrawerOpen(true)} />

        {/* Main Content Area - Sidebar + Story */}
        <Box sx={{ 
          flex: 1,
          display: 'flex',
          overflow: 'hidden'
        }}>
          {/* Desktop Sidebar */}
          {!isMobile && <StoryList />}

          {/* Mobile Drawer */}
          <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': {
                width: '85%',
                maxWidth: '380px',
                backgroundColor: colors.headerBg
              }
            }}
          >
            <StoryList />
          </Drawer>

          {/* Content Area */}
          <Box 
            ref={mainContentRef}
            sx={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
              scrollBehavior: 'smooth'
            }}
          >
          {selectedStory ? (
            <>
            {/* Story Content */}
            <Box 
              ref={contentScrollRef}
              sx={{ 
                flex: 1,
                overflowY: 'auto',
                backgroundColor: colors.bg,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f5f5f5',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#d0d0d0',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: '#b0b0b0',
                  },
                },
              }}>
              <Box sx={{ maxWidth: '680px', mx: 'auto', pt: { xs: 3, md: 6 }, px: { xs: 3, md: 0 } }}>
                {/* Today's Story Banner */}
                {isTodaysStory(selectedStory) && (
                  <Box
                    sx={{
                      mb: 4,
                      px: 3,
                      py: 2,
                      background: isDark 
                        ? 'linear-gradient(135deg, rgba(192, 132, 252, 0.08) 0%, rgba(147, 51, 234, 0.04) 50%, transparent 100%)'
                        : 'linear-gradient(135deg, rgba(147, 51, 234, 0.05) 0%, rgba(192, 132, 252, 0.03) 50%, transparent 100%)',
                      borderLeft: `1px solid ${isDark ? 'rgba(192, 132, 252, 0.3)' : 'rgba(147, 51, 234, 0.25)'}`,
                      borderRadius: '0 8px 8px 0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: isDark
                          ? 'radial-gradient(circle at top left, rgba(192, 132, 252, 0.06) 0%, transparent 60%)'
                          : 'radial-gradient(circle at top left, rgba(147, 51, 234, 0.04) 0%, transparent 60%)',
                        pointerEvents: 'none'
                      }
                    }}
                  >
                    <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography sx={{ fontSize: '1.1rem', opacity: 0.9 }}>✨</Typography>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '0.65rem',
                            fontWeight: 500,
                            color: isDark ? 'rgba(192, 132, 252, 0.7)' : 'rgba(147, 51, 234, 0.7)',
                            letterSpacing: '1.5px',
                            textTransform: 'uppercase',
                            mb: 0.25
                          }}
                        >
                          Fresh Today
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.85rem',
                            fontWeight: 300,
                            color: colors.textSecondary,
                            fontStyle: 'italic'
                          }}
                        >
                          Just published for you
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Story Header */}
                <Box sx={{ mb: 6 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography 
                      variant="overline" 
                      sx={{ 
                        color: colors.textSecondary,
                        letterSpacing: '1.5px',
                        fontSize: '0.65rem',
                        fontWeight: 400,
                        display: 'block'
                      }}
                    >
                      {selectedStory.city} · {format(new Date(selectedStory.publishDate), 'MMMM d, yyyy')}
                    </Typography>
                    <IconButton
                      onClick={handleCopyPermalink}
                      size="small"
                      sx={{
                        color: colors.textMuted,
                        '&:hover': {
                          color: colors.text,
                          backgroundColor: colors.hover
                        }
                      }}
                      title="Copy story link"
                    >
                      <LinkIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography 
                    variant="h1" 
                    sx={{ 
                      fontWeight: 400,
                      color: colors.text,
                      lineHeight: 1.2,
                      letterSpacing: '-0.5px',
                      fontSize: { xs: '2rem', md: '2.5rem' },
                      mb: 1.5
                    }}
                  >
                    {selectedStory.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: colors.textSecondary,
                      fontSize: '0.9rem',
                      fontStyle: 'italic',
                      mb: 2
                    }}
                  >
                    by Marin Pierre
                  </Typography>
                  
                  {/* Engagement Stats */}
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 3, 
                    mb: 3,
                    alignItems: 'center'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <FavoriteIcon sx={{ fontSize: '1.1rem', color: colors.textMuted }} />
                      <Typography sx={{ color: colors.textSecondary, fontSize: '0.85rem' }}>
                        {getEngagementStats(selectedStory.id).likes.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <ShareIcon sx={{ fontSize: '1rem', color: colors.textMuted }} />
                      <Typography sx={{ color: colors.textSecondary, fontSize: '0.85rem' }}>
                        {getEngagementStats(selectedStory.id).shares.toLocaleString()}
                      </Typography>
                    </Box>
                    <Typography sx={{ color: colors.textMuted, fontSize: '0.8rem' }}>
                      {selectedStory.readTime}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                    {selectedStory.tags.map((tag) => {
                      const getTagStyle = (tag: string) => {
                        if (tag === '♀♀') {
                          return {
                            bg: isDark ? 'rgba(219, 112, 147, 0.15)' : 'rgba(219, 112, 147, 0.1)',
                            color: isDark ? '#FFB6C1' : '#C71585'
                          };
                        }
                        if (tag === '♂♂') {
                          return {
                            bg: isDark ? 'rgba(100, 149, 237, 0.15)' : 'rgba(100, 149, 237, 0.1)',
                            color: isDark ? '#87CEEB' : '#4169E1'
                          };
                        }
                        if (tag === '♂♀') {
                          return {
                            bg: isDark ? 'rgba(147, 112, 219, 0.15)' : 'rgba(147, 112, 219, 0.1)',
                            color: isDark ? '#DDA0DD' : '#8B008B'
                          };
                        }
                        return {
                          bg: isDark ? '#333' : '#f5f5f5',
                          color: isDark ? '#999' : '#666'
                        };
                      };

                      const tagStyle = getTagStyle(tag);

                      return (
                        <Typography
                          key={tag}
                          variant="caption"
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            backgroundColor: tagStyle.bg,
                            color: tagStyle.color,
                            borderRadius: '12px',
                            fontSize: '0.65rem',
                            letterSpacing: '0.5px',
                            fontWeight: 500,
                            userSelect: 'none',
                            cursor: 'default'
                          }}
                        >
                          {tag}
                        </Typography>
                      );
                    })}
                  </Box>
                </Box>

                {/* Story Paragraphs */}
                {selectedStory.content.split('\n\n').map((paragraph, index) => (
                  <Typography
                    key={index}
                    paragraph
                    sx={{
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      fontSize: { xs: '0.95rem', md: '1.05rem' },
                      lineHeight: 1.75,
                      color: colors.text,
                      mb: 3,
                      fontWeight: 300,
                      letterSpacing: '0.2px',
                      ...(index === 0 && !isMobile && {
                        '&::first-letter': {
                          float: 'left',
                          fontSize: '4.5rem',
                          lineHeight: '3.5rem',
                          fontWeight: 300,
                          mr: 1.5,
                          mt: 0.5,
                          color: colors.text,
                        },
                      }),
                    }}
                  >
                    {paragraph}
                  </Typography>
                ))}

                {/* Martini Banner */}
                <MartiniBanner />

                {/* Podcast Banner */}
                <Box
                  sx={{
                    mt: 8,
                    mb: 8,
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    component="a"
                    href={STRIPE_PAYMENT_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'block',
                      maxWidth: { xs: '100%', md: '600px' },
                      width: '100%',
                    }}
                  >
                    <Box
                      component="img"
                      src="/podcast.png"
                      alt="Podcast"
                      sx={{
                        width: '100%',
                        height: 'auto',
                        borderRadius: '8px',
                        transition: 'transform 0.3s ease, opacity 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          transform: 'scale(1.02)',
                          opacity: 0.9,
                        },
                      }}
                    />
                  </Box>
                </Box>

                {/* Footer Disclaimer */}
                <Box
                  sx={{
                    mt: 6,
                    mb: 4,
                    pt: 4,
                    borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: '0.7rem', md: '0.75rem' },
                      color: colors.textMuted,
                      lineHeight: 1.8,
                      mb: 2,
                    }}
                  >
                    For readers and listeners 18+ only. Discretion advised.<br />
                    All stories are works of fiction. All characters are entirely fictional.<br />
                    Any resemblance to actual persons is purely coincidental.
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '0.65rem', md: '0.7rem' },
                      color: colors.textMuted,
                      opacity: 0.7,
                    }}
                  >
                    © {new Date().getFullYear()} Daily Stories. All rights reserved.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: '100%',
            color: '#999'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 300 }}>
              Select a story to read
            </Typography>
          </Box>
        )}
        </Box>
        </Box>
      </Box>
    </>
  );
};

export default Home;
